import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTokens } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio, getContrastTextColor } from '../utils/luminance';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a standout wide community photo — exterior, amenity, or interior — that works as a large background plate. Prefer polished, high-quality shots. Skip logos, floor plans, parking lots, utility areas, or low-value shots.',
    context:
      'Larger background image in the Property Overview overlapping pair on the Home page — pair with a different subject in the smaller foreground plate.',
  },
};

const fgImageDef: SlotGroupDefinition = {
  label: 'Foreground Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a standout detail or lifestyle photo — interior vignette, amenity close-up, or inviting space — that works as a smaller overlapping plate. Prefer polished, high-quality shots. Skip logos, floor plans, parking lots, utility areas, or low-value shots.',
    context:
      'Smaller foreground image in the Property Overview overlapping pair on the Home page — must be a different photo and subject from the background plate.',
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Choose Your Home CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'Choose Your Home' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

/**
 * Prefer secondary for text on the primary surface when it clears WCAG AA
 * (4.5:1). Otherwise fall back to black/white via `getContrastTextColor` so
 * every property stays readable regardless of brand palette.
 */
function pickSecondaryOrContrast(
  background: string,
  secondary: string | null | undefined,
): string {
  if (secondary && contrastRatio(secondary, background) >= 4.5) {
    return secondary;
  }
  return getContrastTextColor(background);
}

export function PropertyOverview() {
  const bgImage = useSlotGroup('propertyoverview.bg_image', bgImageDef);
  const fgImage = useSlotGroup('propertyoverview.fg_image', fgImageDef);
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);

  const tokens = useTokens();
  const { titleColor, ctaFill, ctaLabel } = useMemo(() => {
    const primary = tokens?.color?.primary || '#B28C59';
    const secondary = tokens?.color?.secondary;
    // Title is black or white against primary for maximum readability.
    const title = getContrastTextColor(primary);
    // CTA keeps secondary when it clears contrast; otherwise same B/W pick.
    const cta = pickSecondaryOrContrast(primary, secondary);
    return {
      titleColor: title,
      ctaFill: cta,
      ctaLabel: getContrastTextColor(cta),
    };
  }, [tokens]);

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();
  const imageRef = useScrollReveal<HTMLDivElement>();

  return (
    <section
      className="w-full overflow-x-hidden md:overflow-hidden bg-primary"
      style={{
        ['--overview-title' as string]: titleColor,
        ['--overview-cta-fill' as string]: ctaFill,
        ['--overview-cta-label' as string]: ctaLabel,
      }}
    >
      <div className="flex flex-col-reverse md:flex-row md:items-center max-w-[1400px] mx-auto">
        {/* ── Text column ── */}
        <div
          ref={contentRef}
          className="reveal-fade-up relative z-0 w-full md:w-[55%] lg:w-[58%] flex flex-col justify-center items-center text-center px-8 sm:px-12 md:px-10 lg:px-16 xl:px-24 pt-48 pb-14 sm:pt-52 md:py-20 lg:py-24"
        >
          <RichTextContent
            path="propertyoverview.title"
            as="h2"
            className="overview-romance-title text-[1.75rem] sm:text-[1.9rem] md:text-[2.25rem] font-normal leading-[1.2] mb-7 max-w-lg"
          />

          <RichTextContent
            path="propertyoverview.description"
            as="div"
            className="overview-romance-body font-body text-[15px] sm:text-base md:text-[17px] leading-[1.7] max-w-lg mx-auto mb-9 [&_a]:underline [&_p]:mb-4 last:[&_p]:mb-0"
          />

          <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || '/floor-plans'}
              target={ctaLink._target || '_self'}
              className="overview-romance-cta inline-flex items-center justify-center gap-2 px-7 py-3.5 font-body text-[14px] sm:text-[15px] font-bold tracking-wide uppercase transition-all duration-300"
            >
              <span>{ctaLink.label || 'Choose Your Home'}</span>
              <svg
                className="w-3 h-3 flex-shrink-0"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4.5 2L8.5 6L4.5 10"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </EditableSlotGroup>
        </div>

        {/* ── Overlapping image composition ──
            Mobile: bg flush under hero; both plates hang into the black text area.
            Desktop: right-shifted stack with left-overlapping fg. */}
        <div
          ref={imageRef}
          className="reveal-fade-up relative z-[1] w-full md:w-[45%] lg:w-[42%] h-[280px] sm:h-[320px] md:h-[480px] lg:h-[540px] shrink-0 -mb-24 sm:-mb-28 md:mb-0"
        >
          {/* Background plate — top:0 / full-bleed on mobile; right-shifted on md+ */}
          <div className="absolute inset-x-0 top-0 md:inset-x-auto md:right-0 md:top-1/2 md:-translate-y-1/2 w-full md:w-[78%] lg:w-[82%] h-full md:h-[72%] z-[1]">
            <div className="absolute inset-0">
              <EditableSlotGroup groupPath="propertyoverview.bg_image" definition={bgImageDef}>
                {bgImage.image ? (
                  <VibesitesImage
                    url={bgImage.image}
                    alt={bgImage.alt || ''}
                    sizes="(max-width: 768px) 100vw, 35vw"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[var(--overview-title)]/20" />
                )}
              </EditableSlotGroup>
            </div>

            {/* Foreground — mobile: centered, shifted down to straddle into black;
                md+: left-overlap, vertically centered on bg */}
            <div className="overview-romance-fg absolute left-1/2 md:left-0 top-[78%] md:top-1/2 -translate-x-1/2 md:-translate-x-[42%] -translate-y-1/2 w-[70%] max-w-[260px] sm:max-w-[280px] md:max-w-none md:w-[60%] lg:w-[58%] aspect-[368/378] z-[2]">
              <div className="absolute inset-0">
                <EditableSlotGroup groupPath="propertyoverview.fg_image" definition={fgImageDef}>
                  {fgImage.image ? (
                    <VibesitesImage
                      url={fgImage.image}
                      alt={fgImage.alt || ''}
                      sizes="(max-width: 768px) 72vw, 22vw"
                      className="absolute inset-0 w-full h-full object-cover object-center"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-[var(--overview-title)]/30" />
                  )}
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .overview-romance-title {
          font-family: Georgia, 'Times New Roman', serif;
          color: var(--overview-title);
        }
        .overview-romance-body {
          color: #ffffff;
        }
        .overview-romance-body a {
          color: #ffffff;
        }
        .overview-romance-cta {
          background: var(--overview-cta-fill);
          color: var(--overview-cta-label);
          border-radius: 1px;
          box-shadow: none;
        }
        .overview-romance-cta:hover {
          filter: brightness(0.92);
          transform: translateY(-2px);
        }
        .overview-romance-cta:active {
          transform: translateY(0);
        }
        .overview-romance-fg {
          box-shadow: 2.58px -4.29px 18px rgba(84, 82, 79, 0.85);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Welcome to your new home.</p>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue:
        '<p>Discover a higher standard of luxury living — with superior finishes, distinctive design, and bright interiors that welcome you in style.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.bg_image': bgImageDef,
    'propertyoverview.fg_image': fgImageDef,
    'propertyoverview.cta_link': ctaLinkDef,
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PropertyOverview;
